import { useEffect, useMemo, useState } from 'react'

import { KIND_SEARCHABLE_FILTER_WORD } from '@constants'
import { GETAllVariables } from '@types'

import { EVENT_NAME, LocalEventBus, subscribe, unsubscribe } from '../events'

export const useReplaceVariablesInFilter = ({
  rawFilter = '',
  searchableFilterWord,
  inputVariables,
  globalVariables,
  event,
  localEventBus,
}: {
  rawFilter: string | undefined | null
  searchableFilterWord: KIND_SEARCHABLE_FILTER_WORD
  inputVariables: GETAllVariables
  globalVariables: GETAllVariables
  event: EVENT_NAME
  localEventBus: LocalEventBus | undefined
}) => {
  const filter = rawFilter ?? ''
  const [updatedVariables, setUpdatedVariables] = useState<GETAllVariables>(inputVariables)

  // Функция замены НАЗВАНИЯ переменной на ЗНАЧЕНИЕ переменной в фильтре
  const replaceVariablesInFilter = (
    template: string,
    scope: string,
    vars: GETAllVariables
  ): string => {
    return vars.reduce((result, { title, defaultValue, value }) => {
      const token = `${scope}.${title}`

      const variableValue = value ?? defaultValue ?? `'null'`

      const wrapperVariableValue =
        typeof variableValue === 'string' && variableValue.includes(' ')
          ? `'${variableValue}'`
          : variableValue

      return result.includes(token) ? result.replaceAll(token, wrapperVariableValue) : result
    }, template)
  }

  // Обработчик события изменения переменной
  const handleEventVariableChange = (data: CustomEvent) => {
    setUpdatedVariables(prevVariables => {
      const indexReplaceVariable = prevVariables.findIndex(
        variable => variable.id === data.detail.id
      )

      if (indexReplaceVariable < 0) {
        return [...prevVariables, data.detail]
      } else {
        const updatedVariables = [...prevVariables]
        updatedVariables[indexReplaceVariable] = data.detail

        return updatedVariables
      }
    })
  }

  // useEffect отслеживающий изменение переменных формы. Не добавляет переменные, если они уже существуют в состоянии updatedVariables
  useEffect(() => {
    setUpdatedVariables(prevVariables => {
      const filteredArray = inputVariables.filter(variable => {
        return !updatedVariables.some(oldVariable => {
          return oldVariable.id === variable.id
        })
      })

      return [...filteredArray, ...prevVariables]
    })
  }, [inputVariables])

  // useEffect добавляющий подписку на ивент (Локальный который передали в пропсах или глобальный от document)
  useEffect(() => {
    if (localEventBus) {
      localEventBus.subscribe(event, handleEventVariableChange)
    } else {
      subscribe(event, handleEventVariableChange)
    }

    return () => {
      if (localEventBus) {
        localEventBus.unsubscribe(event, handleEventVariableChange)
      } else {
        unsubscribe(event, handleEventVariableChange)
      }
    }
  }, [])

  // Новый фильтр в котором заменили значения переменных для нужных областей видимостей
  const newFilter = useMemo(() => {
    let updated = replaceVariablesInFilter(filter, searchableFilterWord, updatedVariables)
    updated = replaceVariablesInFilter(updated, 'global', globalVariables)

    return updated.includes(`'null'`) ||
      updated.includes('global.') ||
      updated.includes(`${searchableFilterWord}.`)
      ? ''
      : updated
  }, [filter, updatedVariables, globalVariables, searchableFilterWord])

  return {
    updatedFilter: newFilter,
  }
}
